import { useState } from 'react';

import {
  TableBody,
  TableHead,
  TableSortLabel,
  Paper,
  Tooltip,
  Box,
  TableContainer,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { formatDate } from 'utils';
import { ICell, ITableHandlers, SecondaryNodeColor } from 'types';

import { Pagination, IconButton } from '..';

import * as S from './Table.styled';
import * as helpers from './Table.config';

interface IProps<T> {
  data: Array<T>;
  tableHandlers?: Array<ITableHandlers<T>>;
  numerable?: boolean;
}

export const Table = <T extends object & { color?: SecondaryNodeColor }>({
  data,
  tableHandlers,
  numerable,
}: IProps<T>) => {
  const {
    sort,
    paginate,
    formatKey,
    getKeys,
    removeKeyFromObject,
    Direction: { ASC, DESC },
  } = helpers;

  const [order, setOrder] = useState(ASC);
  const [orderBy, setOrderBy] = useState<keyof T>();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const tableHeadDataWithoutColorCell = data.length > 0 && getKeys(data[0], 'color');
  const sortedTableData = data && orderBy ? sort(data, orderBy, order) : data;

  const formatCell = (cell: ICell): string | JSX.Element => {
    switch (true) {
      case cell instanceof Date:
        return formatDate(cell.toString());
      default:
        return String(cell);
    }
  };

  const handleSort = (cell: keyof T) => {
    setOrderBy(cell);

    if (orderBy === cell) {
      setOrder((prev) => (prev === ASC ? DESC : ASC));
    }
  };

  return (
    <Box>
      {data.length > 0 ? (
        <TableContainer>
          <S.MUITable stickyHeader>
            <TableHead>
              <S.HeadRow>
                {numerable && <S.HeadCell>{}</S.HeadCell>}
                {tableHeadDataWithoutColorCell &&
                  tableHeadDataWithoutColorCell.map((cell) => (
                    <S.HeadCell key={`table-cell-${uuidv4()}`}>
                      <Tooltip title="Sortuj">
                        <TableSortLabel
                          active={orderBy === cell}
                          direction={order === ASC ? ASC : DESC}
                          onClick={() => handleSort(cell)}
                        >
                          {formatKey(String(cell))}
                        </TableSortLabel>
                      </Tooltip>
                    </S.HeadCell>
                  ))}
                {tableHandlers && <S.HeadCell>{}</S.HeadCell>}
              </S.HeadRow>
            </TableHead>
            <TableBody>
              {paginate(sortedTableData, page, rowsPerPage).map((row, index) => {
                const rowDataWithoutColorCell = removeKeyFromObject(row, 'color');

                return (
                  <S.BodyRow color={row.color} key={`table-row-${uuidv4()}`}>
                    {numerable && <S.NumerableCell>{index + 1}</S.NumerableCell>}
                    {Object.values(rowDataWithoutColorCell).map((cell: ICell) => (
                      <S.BodyCell component="td" key={uuidv4()}>
                        {formatCell(cell)}
                      </S.BodyCell>
                    ))}
                    {tableHandlers && (
                      <S.BodyCell>
                        <S.Wrapper>
                          {tableHandlers.map(({ handler, icon, ...rest }) => (
                            <IconButton
                              key={`table-handler-${uuidv4()}`}
                              onClick={() => handler(row)}
                              sx={{
                                color: row.color && 'primary.contrastText',
                              }}
                              {...rest}
                            >
                              {icon ?? null}
                            </IconButton>
                          ))}
                        </S.Wrapper>
                      </S.BodyCell>
                    )}
                  </S.BodyRow>
                );
              })}
            </TableBody>
          </S.MUITable>

          {data.length > 5 && (
            <Pagination
              length={data.length}
              rowsPerPage={rowsPerPage}
              page={page}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
            />
          )}
        </TableContainer>
      ) : (
        <S.EmptyInfo component={Paper}>Brak rekordów</S.EmptyInfo>
      )}
    </Box>
  );
};
